import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Play, RotateCcw, SkipBack, SkipForward, Repeat, Mic, MicOff, PlayCircle, PauseCircle } from 'lucide-react';
import { useSequenceStore } from '../store/sequenceStore';
import { synthesizeSpeech, audioEvents } from '../services/pollyService';
import { useSpeechRecognition } from '../context/SpeechRecognitionContext';
import type { SequenceMedia } from '../types';

function SequencePlayer() {
  const navigate = useNavigate();
  const { selectedSequence } = useSequenceStore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(currentIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const { recognition, isListening, startListening, stopListening } = useSpeechRecognition();
  const [lastCommand, setLastCommand] = useState<string | null>(null);
  const [showMicFeedback, setShowMicFeedback] = useState(false);
  const [currentMedia, setCurrentMedia] = useState<SequenceMedia | null>(null);
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [isGifPlaying, setIsGifPlaying] = useState(false);
  const gifRef = useRef<HTMLImageElement>(null);
  const gifContainerRef = useRef<HTMLDivElement>(null);
  const hasInitializedRef = useRef(false);
  const setupCompleteRef = useRef(false);

  // Handle navigation and initialization
  useEffect(() => {
    if (!selectedSequence) {
      console.warn('SequencePlayer: No sequence selected, redirecting to catalog');
      navigate('/');
      return;
    }

    // Reset initialization flags when sequence changes
    hasInitializedRef.current = false;
    setupCompleteRef.current = false;
    setCurrentIndex(0);
    currentIndexRef.current = 0;
  }, [selectedSequence, navigate]);

  // Handle audio events
  useEffect(() => {
    const handleAudioStart = () => {
      console.log('Audio playback started');
      setIsAudioPlaying(true);
      
      if (micEnabled) {
        stopListening();
      }
    };
    
    const handleAudioEnd = () => {
      let message = 'Audio playback ended.';
      setIsAudioPlaying(false);
      
      if (micEnabled) {
        message += ' mic enabled so startListening';
        startListening();
      }
      console.log(message);
    };
    
    audioEvents.on('audioStart', handleAudioStart);
    audioEvents.on('audioEnd', handleAudioEnd);
    
    return () => {
      audioEvents.off('audioStart', handleAudioStart);
      audioEvents.off('audioEnd', handleAudioEnd);
    };
  }, [micEnabled, startListening, stopListening]);

  // Setup speech recognition
  useEffect(() => {
    if (!recognition || !selectedSequence || setupCompleteRef.current) {
      return;
    }

    console.log(`Setting up speech recognition handler`);
    setupSpeechRecognition();
    setupCompleteRef.current = true;

    if (!isListening && micEnabled) {
      console.log('Starting speech recognition');
      startListening();
    }
  }, [recognition, selectedSequence, isListening, startListening, micEnabled]);

  useEffect(() => {
    console.log(`selectedSequence (${selectedSequence?.id}) or currentIndex (${currentIndex}) were changed forcing new load media. `);

    const loadMedia = async () => {
      if (!selectedSequence?.media) return;

      const media = selectedSequence.media.find(m => m.sentenceIndex === currentIndex);
      if (!media) {
        setCurrentMedia(null);
        return;
      }

      setIsMediaLoading(true);
      setMediaError(null);

      try {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = media.url;
        });

        setCurrentMedia(media);
        
        if (media.type === 'gif') {
          const shouldAutoplay = selectedSequence.settings.autoplayGifs;
          setIsGifPlaying(shouldAutoplay);
          
          setTimeout(() => {
            if (!shouldAutoplay && gifContainerRef.current) {
              gifContainerRef.current.classList.add('gif-paused');
              gifContainerRef.current.style.setProperty('--paused-background', `url(${media.url})`);
            }
          }, 0);
        }
      } catch (error) {
        console.error('Error loading media:', error);
        setMediaError('Unable to load media');
        setCurrentMedia(null);
      } finally {
        setIsMediaLoading(false);
      }
    };

    loadMedia();
  }, [currentIndex, selectedSequence]);

  if (!selectedSequence) {
    return null;
  }

  const setupSpeechRecognition = () => {
    if (!recognition) {
      console.error('Speech recognition not available');
      return;
    }
    console.log(`Attach onresult handler in setupSpeechRecognition`);
    recognition.onresult = (event: any) => {
      if (!micEnabled || isAudioPlaying) return;
      
      const results = event.results;
      const lastResultIndex = results.length - 1;
      const transcript = results[lastResultIndex][0].transcript.trim().toLowerCase();
      
      console.log(`Player Heard command: "${transcript}" with confidence ${results[lastResultIndex][0].confidence}`);
      
      setShowMicFeedback(true);
      setTimeout(() => setShowMicFeedback(false), 2000);
      
      setLastCommand(transcript);
      
      const command = substringSearch(transcript);
      switch (command) {
        case 'start': {
          handleStart();
          break;
        }
        case 'next': {
          handleNext();
          break;
        }
        case 'previous': {
          handlePrevious();
          break;
        }
        case 'what':
        case 'repeat': {
          handleRepeat();
          break;
        }
        case 'notfound': {
          console.log('Command not found:', transcript);
          break;
        }
        default: {
          console.log('Unrecognized command:', transcript);
          handleUnrecognized();
        }
      }
      
      setCurrentIndex(currentIndexRef.current);
    };
  };

  const handleStart = async () => {
    setIsPlaying(true);
    currentIndexRef.current = 0;
    setCurrentIndex(0);
    await playCurrentSentence();
  };

  const handleUnrecognized = async () => {
    try {
      await synthesizeSpeech(
        "What?",
        selectedSequence.settings
      );
    } catch (error) {
      console.error('Failed to play sentence:', error);
    }
  };

  const handleNext = async () => {
    if (currentIndexRef.current < selectedSequence.sentences.length - 1) {
      currentIndexRef.current += 1;
      setCurrentIndex(currentIndexRef.current);
      await playCurrentSentence();
    } else {
      try {
        await synthesizeSpeech(
          "The End",
          selectedSequence.settings
        );
      } catch (error) {
        console.error('Failed to play sentence:', error);
      }
    }
  };

  const handlePrevious = async () => {
    if (currentIndexRef.current > 0) {
      currentIndexRef.current -= 1;
      setCurrentIndex(currentIndexRef.current);
      await playCurrentSentence();
    } else {
      currentIndexRef.current = 0;
      setCurrentIndex(0);  
      try {
        await synthesizeSpeech(
          "You're at the beginning",
          selectedSequence.settings
        );
      } catch (error) {
        console.error('Failed to play sentence:', error);
      }
    }
  };

  const handleRepeat = async () => {
    if (currentIndexRef.current >= 0) {
      await playCurrentSentence();
    } else {
      try {
        await synthesizeSpeech(
          "You haven't started yet",
          selectedSequence.settings
        );
      } catch (error) {
        console.error('Failed to play sentence:', error);
      }
    }
  };

  const playCurrentSentence = async () => {
    try {
      await synthesizeSpeech(
        selectedSequence.sentences[currentIndexRef.current],
        selectedSequence.settings,
        selectedSequence
      );
    } catch (error) {
      console.error('Failed to play sentence:', error);
    }
  };

  function substringSearch(input: string): string {
    const match = input.match(/start|next|previous|repeat|what|default/);
    if (match)
      return match[0];
    return "notfound";
  }

  const toggleGifPlayback = () => {
    if (!currentMedia || currentMedia.type !== 'gif' || !gifContainerRef.current) return;

    const newIsPlaying = !isGifPlaying;
    setIsGifPlaying(newIsPlaying);

    if (newIsPlaying) {
      if (gifContainerRef.current) {
        gifContainerRef.current.classList.remove('gif-paused');
        gifContainerRef.current.style.removeProperty('--paused-background');
      }
      if (gifRef.current) {
        const currentSrc = gifRef.current.src;
        gifRef.current.src = '';
        setTimeout(() => {
          if (gifRef.current) {
            gifRef.current.src = currentSrc;
          }
        }, 0);
      }
    } else {
      if (gifContainerRef.current) {
        gifContainerRef.current.classList.add('gif-paused');
        gifContainerRef.current.style.setProperty(
          '--paused-background',
          `url(${currentMedia.url})`
        );
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={() => navigate('/')}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
        >
          <Home size={24} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">{selectedSequence.name}</h1>
        <div className="flex items-center space-x-2">
          <div className={`text-xs ${isAudioPlaying ? 'text-blue-600' : 'text-gray-400'}`}>
            {isAudioPlaying ? 'Playing...' : 'Ready'}
          </div>
          <button
            onClick={() => {
              if(micEnabled){ 
                console.log(`Stopping speech recognition`);
                stopListening();
              } else {
                console.log(`Starting speech recognition`);
                startListening();
              }
              setMicEnabled(!micEnabled);}
            }
            disabled={isAudioPlaying}
            className={`p-2 ${
              micEnabled 
                ? 'text-blue-600 bg-blue-50' 
                : 'text-gray-600 bg-gray-100'
            } rounded-full ${
              isAudioPlaying ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={
              isAudioPlaying 
                ? "Microphone automatically disabled during playback" 
                : micEnabled ? "Disable voice commands" : "Enable voice commands"
            }
          >
            {micEnabled ? <Mic size={24} /> : <MicOff size={24} />}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="mb-8">
          <div className="text-center mb-4">
            <p className="text-2xl font-medium text-gray-900">
              {selectedSequence?.sentences[currentIndex] || "Click Play to start"}
            </p>
          </div>

          {isMediaLoading ? (
            <div className="mt-4 flex justify-center">
              <div className="text-gray-600">Loading media...</div>
            </div>
          ) : mediaError ? (
            <div className="mt-4 flex justify-center">
              <div className="text-red-600">{mediaError}</div>
            </div>
          ) : currentMedia && (
            <div className="mt-4">
              <div 
                ref={gifContainerRef}
                className={`gif-container ${
                  currentMedia.type === 'gif' && !isGifPlaying ? 'gif-paused' : ''
                }`}
                style={
                  currentMedia.type === 'gif' && !isGifPlaying 
                    ? { '--paused-background': `url(${currentMedia.url})` } as React.CSSProperties
                    : undefined
                }
              >
                <img
                  ref={gifRef}
                  src={currentMedia.url}
                  alt=""
                  className="w-full h-full object-contain"
                />
                {currentMedia.type === 'gif' && (
                  <button
                    onClick={toggleGifPlayback}
                    className="gif-control"
                  >
                    {isGifPlaying ? <PauseCircle size={24} /> : <PlayCircle size={24} />}
                  </button>
                )}
              </div>
            </div>
          )}
          
          {micEnabled && (
            <div className="text-center text-sm text-gray-500 mt-2">
              <p>Voice commands: "start", "next", "previous", "repeat"</p>
            </div>
          )}
          
          {micEnabled && showMicFeedback && (
            <div className="text-center mt-2 animate-pulse">
              <div className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                <Mic size={16} className="mr-1" />
                <span className="text-sm font-medium">
                  {lastCommand ? `Heard: "${lastCommand.length>65 ? lastCommand.substring(0,65) + "..." : lastCommand}"` : "Listening..."}
                </span>
              </div>
            </div>
          )}
          
        </div>

        <div className="flex justify-center items-center space-x-6">
          <button
            onClick={handleStart}
            className="flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {isPlaying ? <RotateCcw size={32} /> : <Play size={32} />}
          </button>
        </div>

        <div className="flex justify-center items-center space-x-4 mt-6">
          <button
            onClick={handlePrevious}
            disabled={currentIndex <= 0}
            className="p-3 text-gray-600 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <SkipBack size={24} />
          </button>
          <button
            onClick={handleRepeat}
            className="p-3 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <Repeat size={24} />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= selectedSequence.sentences.length - 1}
            className="p-3 text-gray-600 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <SkipForward size={24} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default SequencePlayer;